import React from 'react';

/**
 * The icon set the kit uses. Inline SVG rather than a font or a package: the
 * app already pulls lucide-react for page content, but chrome icons need to be
 * identical everywhere and never shift as a font loads.
 */
const PATHS = {
  search: 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM13 13l3.5 3.5',
  close: 'M5.5 5.5l9 9M14.5 5.5l-9 9',
  filter: 'M3 5h14M6 10h8M8.5 15h3',
  chevronDown: 'M4 6.5 8 10.5 12 6.5',
  chevronRight: 'M7.5 4.5 11.5 8.5 7.5 12.5',
  sortAsc: 'M10 15.5V5M5.6 9.4 10 5l4.4 4.4',
  sortDesc: 'M10 4.5V15M5.6 10.6 10 15l4.4-4.4',
  sortable: 'M6.6 8.6 10 5.2l3.4 3.4M6.6 11.4 10 14.8l3.4-3.4',
  download: 'M10 3.5v11M5.5 10 10 14.5 14.5 10M4 17h12',
  plus: 'M10 4v12M4 10h12',
  check: 'M4 10.5 8 14.5l8-9',
  alert: 'M10 4.5v6.5M10 14.2v.3M10 2.5 2.5 16.5h15L10 2.5Z',
  info: 'M10 9v5M10 6.2v.3M10 17.5a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15Z',
  inbox: 'M3 11h4l1.5 2.5h3L13 11h4M3 11l2.5-6h9L17 11v5H3v-5Z',
  edit: 'M13.5 3.5l3 3L8 15H5v-3l8.5-8.5Z',
  trash: 'M4 6h12M8 6V4.5h4V6M6 6v10h8V6M8.5 9v4M11.5 9v4',
  eye: 'M10 5c4 0 6.5 5 6.5 5s-2.5 5-6.5 5-6.5-5-6.5-5S6 5 10 5Zm0 3a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z',
  upload: 'M10 13.5V3.5M5.5 8 10 3.5 14.5 8M4 17h12',
  file: 'M11.5 2.5H5.5v15h9V5.5l-3-3ZM11.5 2.5v3h3M7.5 10h5M7.5 13h5',
  more: 'M10 4.6v.3M10 9.85v.3M10 15.1v.3',
  arrowLeft: 'M16 10H4M9 5l-5 5 5 5',
  refresh: 'M16 10a6 6 0 1 1-1.76-4.24M16 4v3.5h-3.5',
} as const;

export type IconName = keyof typeof PATHS;

interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
}

const Icon: React.FC<IconProps> = ({ name, size = 16, className }) => {
  // The chevrons are drawn on a 16-unit grid, everything else on 20.
  const box = name === 'chevronDown' || name === 'chevronRight' ? 16 : 20;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${box} ${box}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d={PATHS[name]} />
    </svg>
  );
};

export default Icon;
